import { DriveStore } from "../../store/reducers";
import { observeStore } from "../store";

const MAX_PUBLISH_DELAY_MS = 100;
const MAX_CONSECUTIVE_PUBLISH_RETRIES = 3;

interface IDriveNativeStateRegistration {
    key: string;
    statePath: string[];
}

export interface IDriveNativeStatePublisherMetrics {
    registeredStateChanges: number;
    coalescedRegisteredChanges: number;
    sentSnapshots: number;
    payloadBytes: number;
}

/**
 * Publishes raw Redux values requested by the DriveNative bridge.
 *
 * DriveNative owns the registration metadata and normalization. This class only
 * observes the registered Redux paths, coalesces their changes, and publishes
 * the latest raw snapshot through the one-way WebView bridge.
 */
export class DriveNativeStatePublisher {
    private readonly metrics: IDriveNativeStatePublisherMetrics = {
        registeredStateChanges: 0,
        coalescedRegisteredChanges: 0,
        sentSnapshots: 0,
        payloadBytes: 0
    };
    private readonly unsubscriptions: (() => void)[] = [];
    private registrations: IDriveNativeStateRegistration[] = [];
    /** Keys whose observed path changed since the last delivered publish. Only these are sent. */
    private readonly dirtyKeys = new Set<string>();
    private publishTimer: ReturnType<typeof setTimeout> | undefined;
    private consecutivePublishRetries = 0;
    private isPublishingEnabled = true;
    private isStarted = false;

    constructor(private readonly store: DriveStore) {}

    /**
     * Reads the native registrations, begins observing only those Redux paths,
     * and immediately publishes their initial raw snapshot.
     */
    start(): void {
        if (this.isStarted || !this.isPublishingEnabled) { return; }

        this.isStarted = true;
        this.registrations = this.getRegistrations();
        this.registrations.forEach(registration => {
            this.unsubscriptions.push(observeStore(
                this.store,
                state => this.getStateFingerprint(state, registration.statePath),
                () => this.handleRegisteredStateChange(registration.key)
            ));
        });
        // The first publish after start (a new document, or publishing re-enabled) carries every key.
        this.registrations.forEach(registration => this.dirtyKeys.add(registration.key));
        this.publishSnapshot();
    }

    /**
     * Stops all registered-path observers and cancels a pending coalesced publish.
     */
    stop(): void {
        this.unsubscriptions.splice(0).forEach(unsubscribe => unsubscribe());
        this.registrations = [];
        this.dirtyKeys.clear();
        this.consecutivePublishRetries = 0;
        this.isStarted = false;
        this.clearPublishTimer();
    }

    /**
     * Enables or disables the entire web-to-native state publisher. Re-enabling
     * restarts the registered observers and publishes a fresh initial snapshot.
     */
    setPublishingEnabled(isEnabled: boolean): void {
        if (this.isPublishingEnabled === isEnabled) { return; }

        this.isPublishingEnabled = isEnabled;
        if (!isEnabled) {
            this.stop();
            this.updateLocalMetrics();
            return;
        }

        this.start();
    }

    getMetrics(): Readonly<IDriveNativeStatePublisherMetrics> {
        return { ...this.metrics };
    }

    /**
     * Runs only when an observed, DriveNative-registered Redux path changes.
     * The first unsent change starts one fixed 100 ms publish deadline. Later
     * changes replace the retained snapshot but never extend that deadline.
     */
    private handleRegisteredStateChange(key: string): void {
        this.metrics.registeredStateChanges++;
        // The observer that fired already knows which path changed (it compares the path's own
        // fingerprint), so remember the key instead of re-reading every path at the deadline.
        this.dirtyKeys.add(key);
        if (!this.isPublishingEnabled || !this.isDriveNativeBridgeAvailable()) { return; }

        if (this.publishTimer !== undefined) {
            this.metrics.coalescedRegisteredChanges++;
            return;
        }

        this.startPublishTimer();
    }

    /**
     * Builds a snapshot from the paths registered when the publisher started and
     * sends it to DriveNative without retaining or awaiting a callback.
     */
    private readonly publishSnapshot = (): void => {
        this.clearPublishTimer();
        if (!this.isPublishingEnabled || !this.isDriveNativeBridgeAvailable()) { return; }

        try {
            const snapshot = this.createSnapshot();
            if (Object.keys(snapshot).length === 0) { return; }

            window.geotabModules?.webState?.publish(snapshot);
            // Cleared only after delivery succeeds, so a failed send is retried with the same keys.
            Object.keys(snapshot).forEach(key => this.dirtyKeys.delete(key));
            this.consecutivePublishRetries = 0;
            this.metrics.sentSnapshots++;
            this.metrics.payloadBytes += JSON.stringify(snapshot).length;
            this.updateLocalMetrics();
        } catch {
            if (this.consecutivePublishRetries < MAX_CONSECUTIVE_PUBLISH_RETRIES) {
                this.consecutivePublishRetries++;
                this.startPublishTimer();
            }
            return;
        }
    };

    /**
     * Retrieves and validates the serializable path registrations injected by
     * DriveNative. Invalid entries are ignored rather than allowing a malformed
     * bridge registration to break Drive's Redux subscription lifecycle.
     */
    private getRegistrations(): IDriveNativeStateRegistration[] {
        const registrations = window.geotabModules?.webState?.getRegistrations();
        if (!Array.isArray(registrations)) { return []; }

        return registrations.filter(registration => this.isValidRegistration(registration));
    }

    /**
     * Reads the latest raw value of each key whose path changed since the last delivered publish.
     * Keys that changed inside one deadline go out together; unchanged keys are never included. The
     * property keys are supplied by DriveNative and are preserved in the outgoing snapshot.
     */
    private createSnapshot(): Record<string, unknown> {
        const snapshot: Record<string, unknown> = Object.create(null);
        const state = this.store.getState() as unknown;
        this.registrations.forEach(registration => {
            if (this.dirtyKeys.has(registration.key)) {
                snapshot[registration.key] = this.getValueAtPath(state, registration.statePath);
            }
        });
        return snapshot;
    }

    private isValidRegistration(registration: unknown): registration is IDriveNativeStateRegistration {
        if (!registration || typeof registration !== "object" || Array.isArray(registration)) { return false; }

        const { key, statePath } = registration as { key?: unknown; statePath?: unknown };
        return typeof key === "string"
            && key.length > 0
            && Array.isArray(statePath)
            && statePath.length > 0
            && statePath.every(pathSegment => typeof pathSegment === "string" && pathSegment.length > 0);
    }

    private getValueAtPath(state: unknown, statePath: string[]): unknown {
        return statePath.reduce<unknown>((value, pathSegment) => {
            if (!value || typeof value !== "object" || Array.isArray(value)) { return undefined; }
            return (value as Record<string, unknown>)[pathSegment];
        }, state);
    }

    /**
     * Produces an immutable observation value for a registered Redux path.
     * Some legacy state objects can be changed in place, which would otherwise
     * keep the same reference and bypass observeStore's reference precondition.
     */
    private getStateFingerprint(state: unknown, statePath: string[]): string | undefined {
        try {
            return JSON.stringify(this.getValueAtPath(state, statePath));
        } catch {
            return undefined;
        }
    }

    private isDriveNativeBridgeAvailable(): boolean {
        return window.__geotabDriveNativeReactNativeBridge === true
            && typeof window.ReactNativeWebView?.postMessage === "function"
            && typeof window.geotabModules?.webState?.getRegistrations === "function"
            && typeof window.geotabModules?.webState?.publish === "function";
    }

    private startPublishTimer(): void {
        this.publishTimer = setTimeout(this.publishSnapshot, MAX_PUBLISH_DELAY_MS);
    }

    private clearPublishTimer(): void {
        if (this.publishTimer !== undefined) { clearTimeout(this.publishTimer); }
        this.publishTimer = undefined;
    }

    private updateLocalMetrics(): void {
        if (window.DRIVE_DEBUG?.ENABLE_LOGGING) {
            window.DRIVE_DEBUG.DRIVE_NATIVE_STATE_PUBLISHER_METRICS = this.getMetrics();
        }
    }
}
